import type { Stairwell } from '../types/stairwells';
import type { Unit } from '../types/units';
import { naturalCompare } from './naturalSort';

export const STAIRWELL_NAME_MAX_LENGTH = 50;

/** Natural order by name: A, B, …, 2 before 10. */
export const sortedStairwells = (stairwells: { [key: string]: Stairwell }): Stairwell[] =>
    Object.values(stairwells).sort((a, b) => naturalCompare(a.name, b.name));

/** "Uppg. A" — the short form in lists and lease rows. */
export const stairwellLabel = (name: string): string => `Uppg. ${name}`;

/** The unit's stairwell name; undefined without one or when the key dangles. */
export const unitStairwellName = (
    unit: Pick<Unit, 'stairwellKey'>,
    stairwells: { [key: string]: Pick<Stairwell, 'name'> }
): string | undefined => (unit.stairwellKey ? stairwells[unit.stairwellKey]?.name : undefined);

/**
 * Trimmed name, or throws the Swedish error: empty, too long, or (ignoring
 * case) already taken by another stairwell of the building.
 */
export const validateStairwellName = (
    name: string,
    stairwells: { [key: string]: Pick<Stairwell, 'name'> },
    exceptKey?: string
): string => {
    const trimmed = name.trim();
    if (!trimmed) {
        throw new Error('Ange ett namn på trappuppgången');
    }
    if (trimmed.length > STAIRWELL_NAME_MAX_LENGTH) {
        throw new Error(`Namnet får vara högst ${STAIRWELL_NAME_MAX_LENGTH} tecken`);
    }
    const lower = trimmed.toLocaleLowerCase('sv');
    const taken = Object.entries(stairwells).some(([key, stairwell]) =>
        key !== exceptKey && stairwell.name.trim().toLocaleLowerCase('sv') === lower);
    if (taken) {
        throw new Error(`Det finns redan en trappuppgång som heter ${trimmed}`);
    }
    return trimmed;
};
